import { Fragment, useCallback, useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { Layout, Table, Radio, DatePicker, Button, message, Flex, Space, Tooltip } from 'antd';
import { fullPagerConfig } from '@/utils/property';
import ActiveUserTrend from './ActiveUserTrend';
import { get } from '@/core/request';
import { lastDays, timeString, todayDateString } from '@/utils/utils';
import dayjs from 'dayjs';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import PageHeader from '@components/PageHeader/PageHeader';
import './ActiveUser.less';

const { RangePicker } = DatePicker;
export default function ActiveUser() {
    // 渲染table数据的state
    const [tableData, setTableData] = useState({});
    const [chartData, setChartData] = useState({
        xData: [],
        yData: [],
    });

    // 分页设置
    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.active-user-table-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const dateFormat = 'YYYY-MM-DD';

    const date = new Date();
    // 当天
    const dateString = todayDateString(date);
    // 七天前   30天前
    const [dateStringStart, setDateStringStart] = useState(dateString);
    // 当天
    const [dateStringEnd, setDateStringEnd] = useState(dateString);

    const [timeRadio, setTimeRadio] = useState('today');

    // 改变一页有多少数据
    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    // 改变第几页
    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    // 渲染table数据
    useEffect(() => {
        var days = (Date.parse(dateStringEnd) - Date.parse(dateStringStart)) / (1 * 24 * 60 * 60 * 1000);
        if (days > 90) {
            message.error('时间区间不得超过90天');
            return;
        }
        get(`/user/data/statistics/active-users-detail`, {
            endTime: dateStringEnd,
            startTime: dateStringStart,
            page: currPage,
            limit: pageSize,
        }).then(res => {
            if (res.status_code === 200) {
                const xArr = [];
                const yArr = [];
                const { chartData, pageData } = res.data;
                const { list } = pageData;
                chartData
                    && chartData.forEach(item => {
                        xArr.push(item.time);
                        yArr.push(item.num);
                    });
                const arr =
                    list
                    && list.map(item => {
                        return {
                            ...item,
                            key: item.time,
                        };
                    });
                setTableData({
                    ...pageData,
                    list: arr,
                });
                setChartData({
                    xData: xArr,
                    yData: yArr,
                });
            }
        });
    }, [dateStringEnd, dateStringStart, currPage, pageSize]);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData.totalCount,
        onShowSizeChange: (current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    // 改变时间
    const handleChangeValue = e => {
        setTimeRadio(e.target.value);
        let startdate = '';
        if (e.target.value === 'today') {
            setDateStringStart(dateString);
            setDateStringEnd(dateString);
            changeCurrPage(1);
            return;
        } else if (e.target.value === 'seven') {
            startdate = lastDays(6);
        } else {
            startdate = lastDays(29);
        }
        setDateStringStart(startdate);
        setDateStringEnd(dateString);
        changeCurrPage(1);
    };

    // 搜索的自定义时间 控制为90天

    const disabledDate = current => {
        return current > dayjs().endOf('day');
    };

    const handleChangeTime = (value, dateString) => {
        setDateStringStart(dateString[0]);
        setDateStringEnd(dateString[1]);
        setTimeRadio('');
        changeCurrPage(1);
    };

    // 导出数据
    const handleExportFile = () => {
        var days = (Date.parse(dateStringEnd) - Date.parse(dateStringStart)) / (1 * 24 * 60 * 60 * 1000);
        if (days > 90) {
            message.error('时间区间不得超过90天');
            return;
        }
        get(
            `/user/data/statistics/active-users-download`,
            {
                endTime: dateStringEnd,
                startTime: dateStringStart,
                page: currPage,
                limit: pageSize,
            },
            { responseType: 'blob' },
        ).then(res => {
            if (res.size !== 0) {
                message.success('请稍后，正在导出');
                const date = new Date();
                const dateString = timeString(date);
                const fileName = `活跃用户统计${dateString}.xlsx`;
                const a = document.createElement('a');
                const event = new MouseEvent('click');
                a.download = fileName;
                const objectUrl = URL.createObjectURL(res);
                a.href = objectUrl;
                a.dispatchEvent(event);
                URL.revokeObjectURL(objectUrl);
            } else {
                message.warning('当前列表没有数据，无法导出');
            }
        });
    };

    const onRefresh = () => {
        var days = (Date.parse(dateStringEnd) - Date.parse(dateStringStart)) / (1 * 24 * 60 * 60 * 1000);
        if (days > 90) {
            message.error('时间区间不得超过90天');
            return;
        }
        get(`/user/data/statistics/active-users-detail`, {
            endTime: dateStringEnd,
            startTime: dateStringStart,
            page: currPage,
            limit: pageSize,
        }).then(res => {
            if (res.status_code === 200) {
                const xArr = [];
                const yArr = [];
                const { chartData, pageData } = res.data;
                const { list } = pageData;
                chartData
                    && chartData.forEach(item => {
                        xArr.push(item.time);
                        yArr.push(item.num);
                    });
                const arr =
                    list
                    && list.map(item => {
                        return {
                            ...item,
                            key: item.time,
                        };
                    });
                setTableData({
                    ...pageData,
                    list: arr,
                });
                setChartData({
                    xData: xArr,
                    yData: yArr,
                });
            }
        });
    };

    // table 数据相关
    const columns = [
        { title: '日期', dataIndex: 'time', key: 'time' },
        {
            title: '活跃用户数',
            dataIndex: 'num',
            render: (operates, row) => <span>{operates === null ? 0 : operates}</span>,
        },
    ];

    return (
        <div className="page-container active-user-page">
            <PageHeader title="活跃用户统计" breadcrumb={[{ label: '用户中心', path: '/uc' }, { label: '用户数据' }]} />

            <div className="active-user-content">
                <div className="active-user-title">统计时间</div>

                <div className="bi-user-overview-time-box">
                    <Radio.Group onChange={handleChangeValue} size="medium" value={timeRadio} className="bi-user-overview-time-box-left">
                        <Radio.Button value="today">今天</Radio.Button>
                        <Radio.Button value="seven">最近7天</Radio.Button>
                        <Radio.Button value="thirty">最近30天</Radio.Button>
                    </Radio.Group>
                    {dateStringStart && <RangePicker size="medium" allowClear={false} value={[dayjs(dateStringStart, dateFormat), dayjs(dateStringEnd, dateFormat)]} disabledDate={disabledDate} onChange={handleChangeTime} style={{ width: 260 }} />}
                </div>

                <div className="active-user-title">活跃用户趋势</div>

                <ActiveUserTrend chartData={chartData}></ActiveUserTrend>

                <div ref={tableRegionRef} className="active-user-table-region">
                    <div className="main-toolbar active-user-table-toolbar">
                        <div className="main-tools">
                            <span className="table-title">数据明细</span>
                        </div>
                        <div className="sub-tools">
                            <Space>
                                <Button type="default" onClick={handleExportFile} icon={<i className="bi bi-download"></i>}>
                                    导出
                                </Button>
                                <Tooltip title="刷新列表">
                                    <Button type="default" onClick={onRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                                </Tooltip>
                                <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                    <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                                </Tooltip>
                            </Space>
                        </div>
                    </div>

                    <div className="active-user-table-wrap">
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            bordered
                            size="medium"
                            columns={columns}
                            dataSource={tableData.list}
                            scroll={{
                                x: 'max-content',
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                            sticky
                            pagination={paginationProps}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
